import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Description as DocumentIcon,
  Reply as ReplyIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  MailOutline as MailOutlineIcon,
  Folder as PortalIcon,
  Assignment as TicketIcon,
} from '@mui/icons-material';
import { adressenApi, ticketsApi } from '../services/api';
import type { AdresseDetail, AdresseEmail, AdresseDokument, AdressePortalDokument, Ticket } from '../types/api';
import FristenWidget from './FristenWidget';
import KundenHistorie from './KundenHistorie';

interface AdresseDetailDialogProps {
  open: boolean;
  adresseId: number | null;
  onClose: () => void;
}

const AdresseDetailDialog: React.FC<AdresseDetailDialogProps> = ({
  open,
  adresseId,
  onClose,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<AdresseDetail | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [expandedSection, setExpandedSection] = useState<string | false>('eingehend');

  const handleEmailClick = (emailId: number) => {
    onClose();
    navigate(`/emails?id=${emailId}`);
  };

  const handleDokumentClick = (dokumentId: number) => {
    onClose();
    // Zur Dokument-Bearbeitungsseite navigieren
    navigate(`/dokumente/${dokumentId}`);
  };

  const handleTicketClick = (ticketId: number) => {
    onClose();
    navigate(`/tickets?id=${ticketId}`);
  };

  useEffect(() => {
    if (open && adresseId) {
      loadDetails();
      loadTickets();
    } else {
      setDetails(null);
      setTickets([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adresseId]);

  const loadDetails = async () => {
    if (!adresseId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await adressenApi.getDetails(adresseId);
      setDetails(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Adress-Details:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    if (!adresseId) return;

    try {
      const response = await ticketsApi.getAll({ adresseId: adresseId, take: 100 });
      setTickets(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Tickets:', err);
    }
  };

  const handleAccordionChange = (panel: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderEmailList = (emails: AdresseEmail[], emptyMessage: string) => {
    if (emails.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List dense disablePadding>
        {emails.map((email, index) => (
          <React.Fragment key={email.emailID}>
            {index > 0 && <Divider />}
            <ListItem
              onClick={() => handleEmailClick(email.emailID)}
              sx={{
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={email.gelesen ? 'normal' : 'bold'}>
                      {email.betreff || '(Kein Betreff)'}
                    </Typography>
                    {email.wichtig && (
                      <Chip label="Wichtig" size="small" color="error" variant="outlined" />
                    )}
                    {!email.gelesen && (
                      <Chip label="Ungelesen" size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {email.absenderName || email.absenderEmail || 'Unbekannt'} - {formatDate(email.empfangenAm)}
                    </Typography>
                    {email.zusammenfassung && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {email.zusammenfassung}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderDokumentList = (dokumente: AdresseDokument[], emptyMessage: string) => {
    if (dokumente.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List dense disablePadding>
        {dokumente.map((dok, index) => (
          <React.Fragment key={dok.dokumentID}>
            {index > 0 && <Divider />}
            <ListItem
              onClick={() => handleDokumentClick(dok.dokumentID)}
              sx={{
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {dok.betreff || '(Kein Betreff)'}
                    </Typography>
                    <Chip
                      label={dok.status}
                      size="small"
                      color={dok.status === 'Entwurf' ? 'warning' : dok.status === 'Versendet' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Erstellt: {formatDate(dok.erstelltAm)}
                      {dok.versendetAm && ` | Versendet: ${formatDate(dok.versendetAm)}`}
                    </Typography>
                    {dok.zusammenfassung && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {dok.zusammenfassung}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  const getSectionCount = (section: string): number => {
    if (!details && section !== 'tickets') return 0;
    switch (section) {
      case 'eingehend': return details?.eingehendeEmails.length || 0;
      case 'ausgehend': return details?.ausgehendeEmails.length || 0;
      case 'briefe': return details?.briefe.length || 0;
      case 'antwortbriefe': return details?.antwortbriefe.length || 0;
      case 'hochgeladen': return details?.hochgeladeneDokumente.length || 0;
      case 'portal': return details?.portalDokumente?.length || 0;
      case 'tickets': return tickets.length;
      default: return 0;
    }
  };

  const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'default' => {
    switch (status) {
      case 'Offen': return 'error';
      case 'In Bearbeitung': return 'warning';
      case 'Erledigt': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (prioritaet?: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (prioritaet) {
      case 'Dringend':
      case 'Hoch': return 'error';
      case 'Mittel': return 'warning';
      case 'Niedrig': return 'info';
      default: return 'default';
    }
  };

  const renderTicketList = (ticketList: Ticket[], emptyMessage: string) => {
    if (ticketList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List dense disablePadding>
        {ticketList.map((ticket, index) => (
          <React.Fragment key={ticket.ticketID}>
            {index > 0 && <Divider />}
            <ListItem
              onClick={() => handleTicketClick(ticket.ticketID)}
              sx={{
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {ticket.titel}
                    </Typography>
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={getStatusColor(ticket.status)}
                      variant="outlined"
                    />
                    {ticket.prioritaet && (
                      <Chip
                        label={ticket.prioritaet}
                        size="small"
                        color={getPriorityColor(ticket.prioritaet)}
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Erstellt: {formatDate(ticket.erstelltAm)}
                      {ticket.wann && ` | Fällig: ${formatDate(ticket.wann)}`}
                    </Typography>
                    {ticket.was && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '400px',
                        }}
                      >
                        {ticket.was}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderPortalDokumentList = (dokumente: AdressePortalDokument[], emptyMessage: string) => {
    if (!dokumente || dokumente.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List dense disablePadding>
        {dokumente.map((dok, index) => (
          <React.Fragment key={dok.portalDokumentID}>
            {index > 0 && <Divider />}
            <ListItem
              sx={{
                py: 1,
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {dok.titel || '(Kein Titel)'}
                    </Typography>
                    <Chip
                      label={dok.kategorie}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                    <Chip
                      label={dok.vomPortalHochgeladen ? 'Vom Portal' : 'Freigegeben'}
                      size="small"
                      color={dok.vomPortalHochgeladen ? 'info' : 'success'}
                      variant="outlined"
                    />
                    <Chip
                      label={dok.status}
                      size="small"
                      color={dok.status === 'Neu' ? 'warning' : dok.status === 'Bearbeitet' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {dok.dateiName} ({formatFileSize(dok.dateiGroesse)})
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {dok.vomPortalHochgeladen && dok.hochgeladenAm
                        ? `Hochgeladen: ${formatDate(dok.hochgeladenAm)}`
                        : dok.freigegebenAm
                        ? `Freigegeben: ${formatDate(dok.freigegebenAm)}`
                        : ''}
                    </Typography>
                    {dok.beschreibung && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        {dok.beschreibung}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Adress-Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : details ? (
          <Box>
            {/* Adress-Informationen */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {details.adresse.firma ? (
                  <BusinessIcon fontSize="large" color="primary" />
                ) : (
                  <PersonIcon fontSize="large" color="primary" />
                )}
                <Box>
                  <Typography variant="h6">
                    {details.adresse.anrede} {details.adresse.titel} {details.adresse.vollstaendigerName}
                  </Typography>
                  {details.adresse.firma && (
                    <Typography variant="body2" color="text.secondary">
                      {details.adresse.firma}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={3}>
                {details.adresse.email && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <MailOutlineIcon fontSize="small" color="action" />
                    <Typography variant="body2">{details.adresse.email}</Typography>
                  </Box>
                )}
                {details.adresse.telefon && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{details.adresse.telefon}</Typography>
                  </Box>
                )}
                {(details.adresse.strasse || details.adresse.ort) && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {[
                        details.adresse.strasse,
                        details.adresse.hausnummer,
                        details.adresse.plz,
                        details.adresse.ort,
                        details.adresse.land,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Fristen-Widget (nur für Mandanten) */}
            {details.adresse.istMandant && (
              <Box sx={{ mb: 3 }}>
                <FristenWidget
                  adresseId={details.adresse.adresseID}
                  adresseName={details.adresse.vollstaendigerName || details.adresse.firma}
                  compact
                />
              </Box>
            )}

            {/* Kunden-Historie (Timeline) */}
            {details.adresse.istMandant && (
              <Box sx={{ mb: 3 }}>
                <KundenHistorie adresseId={details.adresse.adresseID} compact />
              </Box>
            )}

            {/* Sektionen mit Accordion */}
            <Accordion
              expanded={expandedSection === 'eingehend'}
              onChange={handleAccordionChange('eingehend')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="primary" />
                  <Typography>Eingehende E-Mails</Typography>
                  <Chip label={getSectionCount('eingehend')} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderEmailList(details.eingehendeEmails, 'Keine eingehenden E-Mails vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'ausgehend'}
              onChange={handleAccordionChange('ausgehend')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SendIcon color="secondary" />
                  <Typography>Ausgehende E-Mails</Typography>
                  <Chip label={getSectionCount('ausgehend')} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderEmailList(details.ausgehendeEmails, 'Keine ausgehenden E-Mails vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'briefe'}
              onChange={handleAccordionChange('briefe')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DocumentIcon color="info" />
                  <Typography>Briefe</Typography>
                  <Chip label={getSectionCount('briefe')} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderDokumentList(details.briefe, 'Keine Briefe vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'antwortbriefe'}
              onChange={handleAccordionChange('antwortbriefe')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ReplyIcon color="success" />
                  <Typography>Antwortbriefe</Typography>
                  <Chip label={getSectionCount('antwortbriefe')} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderDokumentList(details.antwortbriefe, 'Keine Antwortbriefe vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'hochgeladen'}
              onChange={handleAccordionChange('hochgeladen')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <UploadIcon color="warning" />
                  <Typography>Hochgeladene Dokumente</Typography>
                  <Chip label={getSectionCount('hochgeladen')} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderDokumentList(details.hochgeladeneDokumente, 'Keine hochgeladenen Dokumente vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'portal'}
              onChange={handleAccordionChange('portal')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PortalIcon sx={{ color: '#9c27b0' }} />
                  <Typography>Portal-Dokumente</Typography>
                  <Chip label={getSectionCount('portal')} size="small" color="secondary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderPortalDokumentList(details.portalDokumente || [], 'Keine Portal-Dokumente vorhanden')}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSection === 'tickets'}
              onChange={handleAccordionChange('tickets')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TicketIcon color="primary" />
                  <Typography>Zugeordnete Tickets</Typography>
                  <Chip label={getSectionCount('tickets')} size="small" color="primary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {renderTicketList(tickets, 'Keine Tickets zugeordnet')}
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdresseDetailDialog;
